import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty } from '../../components/ui';
import { Kpis, Panel, BarsH, BarsV } from '../../components/charts';

/* ============================================================
   Dashboard proc.

   Modul ini menjawab satu kalimat: material mana yang akan
   terlambat, dan karena siapa. Karena itu papan tidak berhenti di
   jumlah paket — ia mengikuti satu rantai sebab:

       MAR terlambat -> PO mundur -> pabrikasi mundur ->
       material tiba mundur

   Kolom "tertahan karena" menyalin kalimat penghalang dari API apa
   adanya. Kata "tertahan" sendirian memaksa orang menelepon tiga
   bagian untuk mencari tahu bagian mana yang harus bergerak.
   ============================================================ */

export type PoState = 'BLOCKED' | 'READY' | 'ISSUED' | 'CANCELLED';

export type ProcRow = {
  id: string;
  code: string;
  name: string;
  boqCodes: string[];
  orderedQty: number;
  unit: string;
  poState: PoState;
  poNo: string | null;
  deliveredQty: number;
  shortfallQty: number;
  pct: number;
  noteCount: number;
  onSitePlanned: string | null;
  onSiteForecast: string | null;
  slipDays: number;
  blockers: string[];
};

type Summary = {
  packages: {
    total: number;
    poIssued: number;
    poBlocked: number;
    delivering: number;
    onSite: number;
    late: number;
  };
  slip: { totalWorkingDays: number; worst: { packageCode: string; days: number } | null };
  steps: { step: string; label: string; reached: number; total: number }[];
  marImpact: {
    packageCode: string;
    marPlannedOn: string | null;
    marActualOn: string | null;
    shiftDays: number;
    basis: string;
  }[];
  rows: ProcRow[];
};

export const PO_STATE: Record<string, string> = {
  BLOCKED: 'Tertahan',
  READY: 'Siap terbit',
  ISSUED: 'Terbit',
  CANCELLED: 'Dibatalkan',
};

export const PO_CHIP: Record<string, string> = {
  BLOCKED: 'crit',
  READY: 'blue',
  ISSUED: 'good',
  CANCELLED: '',
};

export const MAR_STATE: Record<string, string> = {
  DRAFT: 'Draf',
  SUBMITTED: 'Diserahkan',
  UNDER_REVIEW: 'Sedang ditinjau',
  APPROVED: 'Disetujui',
  APPROVED_WITH_COMMENT: 'Disetujui dengan catatan',
  REVISE_AND_RESUBMIT: 'Revisi dan serahkan ulang',
  REJECTED: 'Ditolak',
};

export const fmtQty = (v: number): string => v.toLocaleString('id-ID', { maximumFractionDigits: 4 });

export const qtyOf = (v: number, unit: string): string => `${fmtQty(v)} ${unit}`;

/**
 * Slip hanya boleh dibaca kalau kedua tanggal ON SITE ada.
 *
 * Paket tanpa milestone ON SITE di programme pulang dengan slipDays
 * nol, dan nol itu bukan "tepat waktu" melainkan "tidak terhitung".
 * Keduanya terlihat sama persis di layar, dan hanya salah satunya
 * yang boleh dipakai untuk menjanjikan tanggal.
 */
export function slipMeasured(r: { onSitePlanned: string | null; onSiteForecast: string | null }): boolean {
  return r.onSitePlanned != null && r.onSiteForecast != null;
}

/** Kalimat penghalang datang jadi dari API dan tidak ditulis ulang di sini. */
export function Blockers({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="hint">—</span>;
  return (
    <>
      {items.map((b) => (
        <div key={b}>{b}</div>
      ))}
    </>
  );
}

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Summary>(`/proc/summary?projectId=${projectId}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the procurement summary.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const { packages, slip, rows } = data;
  const complete = rows.filter((r) => r.orderedQty > 0 && r.shortfallQty === 0).length;
  const unmeasured = rows.filter((r) => !slipMeasured(r)).length;
  const notes = rows.reduce((s, r) => s + r.noteCount, 0);
  const forecasting = data.marImpact.some((m) => m.basis === 'FORECAST_FROM_TODAY');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Procurement</h1>
          <p>
            Persetujuan melepas pesanan, pesanan melepas pabrik, pabrik melepas site. Papan ini
            membaca rantai itu dari ujung yang paling awal.
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty
          title="Belum ada paket procurement"
          body="Paket dibuat dari kode BOQ yang sudah dimekarkan, lalu ditautkan ke MAR-nya dan ke tujuh langkah di programme. Sebelum itu tidak ada rantai yang bisa diikuti."
        />
      ) : (
        <>
          <Kpis
            items={[
              {
                label: 'Paket',
                value: String(packages.total),
                note: `${packages.onSite} tiba di site · ${packages.delivering} sedang dikirim`,
              },
              {
                label: 'PO terbit',
                value: String(packages.poIssued),
                note:
                  packages.poIssued === 0
                    ? 'belum satu pun paket dipesan'
                    : `dari ${packages.total} paket`,
                tone: packages.poIssued === 0 ? 'warn' : undefined,
              },
              {
                label: 'Tertahan',
                value: String(packages.poBlocked),
                note:
                  packages.poBlocked > 0
                    ? 'gerbang MAR belum terbuka'
                    : 'gerbang MAR terbuka pada semua paket',
                tone: packages.poBlocked > 0 ? 'crit' : 'good',
              },
              {
                label: 'Terkirim penuh',
                value: String(complete),
                note: notes === 0 ? 'belum ada surat jalan' : `${notes} surat jalan tercatat`,
              },
              {
                label: 'Slip terburuk',
                value: slip.worst
                  ? `${slip.worst.days} hari`
                  : unmeasured === rows.length
                    ? '—'
                    : '0 hari',
                note: slip.worst
                  ? `${slip.worst.packageCode} · total ${slip.totalWorkingDays} hari kerja`
                  : unmeasured === rows.length
                    ? 'milestone ON SITE belum ada di programme'
                    : unmeasured > 0
                      ? `${unmeasured} paket belum terukur`
                      : 'semua paket masih pada rencananya',
                tone: slip.worst ? 'crit' : unmeasured > 0 ? 'warn' : 'good',
              },
            ]}
          />

          <Panel
            title="Corong tujuh langkah"
            note="Setiap paket berdiri di satu langkah. ON SITE adalah milestone, bukan pekerjaan: paket terhitung sampai di sana ketika ia benar-benar berada di sana."
            wide
          >
            <BarsV
              labels={data.steps.map((s) => s.label)}
              a={data.steps.map((s) => s.reached)}
              b={data.steps.map((s) => s.total)}
              aName="Paket yang sampai"
              bName="Seluruh paket"
            />
            {packages.poIssued === 0 && (
              <p className="panel-foot">
                Belum satu pun PO terbit, jadi seluruh {packages.total} paket masih berdiri di depan
                langkah pertama. Corong yang datar di sini bukan corong yang kosong.
              </p>
            )}
          </Panel>

          <Panel
            title="Dampak MAR pada tanggal PO"
            note="Hari kerja yang hilang di persetujuan MAR, per paket. Ini sebab pertama di rantai, dan sisanya mengikutinya."
            wide
          >
            {data.marImpact.length === 0 ? (
              <p className="hint">
                Belum ada paket yang punya tanggal rencana persetujuan MAR, jadi pergeserannya belum
                bisa dihitung sama sekali. Batang nol di sini akan terbaca sebagai MAR yang tepat
                waktu, dan itu bukan yang diketahui.
              </p>
            ) : (
              <>
                <BarsH
                  rows={data.marImpact.map((m) => ({
                    label: m.packageCode,
                    value: m.shiftDays,
                    tone:
                      m.shiftDays === 0 ? 'good' : m.basis === 'FORECAST_FROM_TODAY' ? 'crit' : 'warn',
                  }))}
                  suffix=" hari"
                />
                {forecasting && (
                  <p className="panel-foot">
                    Paket yang MAR-nya belum disetujui dihitung sampai hari ini, jadi angkanya masih
                    bertambah setiap hari kerja sampai persetujuannya turun.
                  </p>
                )}
              </>
            )}
          </Panel>

          <Panel
            title="Pengiriman terhadap kuantitas pesanan"
            note="Diurutkan dari yang paling terlambat. Klik satu baris untuk melihat tujuh langkahnya dan surat jalannya."
            wide
          >
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Paket</th>
                    <th>Nama</th>
                    <th>PO</th>
                    <th>Dipesan</th>
                    <th>Terkirim</th>
                    <th>Kurang</th>
                    <th>Surat jalan</th>
                    <th>Slip</th>
                    <th>Tertahan karena</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} onClick={() => nav(`/packages/${r.id}`)}>
                      <td className="num">{r.code}</td>
                      <td>{r.name}</td>
                      <td>
                        <span className={`chip ${PO_CHIP[r.poState] ?? ''}`}>
                          {PO_STATE[r.poState] ?? r.poState}
                        </span>
                      </td>
                      {/* Kuantitas nol berarti belum ditetapkan, bukan nol
                          yang terukur, dan kekurangan kirim terhadapnya
                          tidak berarti apa-apa. */}
                      <td>{r.orderedQty > 0 ? qtyOf(r.orderedQty, r.unit) : '—'}</td>
                      <td>{qtyOf(r.deliveredQty, r.unit)}</td>
                      <td className={r.orderedQty > 0 && r.shortfallQty > 0 ? 'neg' : ''}>
                        {r.orderedQty > 0 ? qtyOf(r.shortfallQty, r.unit) : '—'}
                      </td>
                      <td>{r.noteCount}</td>
                      <td className={slipMeasured(r) && r.slipDays > 0 ? 'neg' : ''}>
                        {slipMeasured(r) ? `${r.slipDays} hari` : '—'}
                      </td>
                      <td>
                        <Blockers items={r.blockers} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}
